/**
 * Dashboard — Portfolio Overview.
 *
 * Sits above the existing dashboard cards. Reads from the projects + clients
 * already loaded by Dashboard.jsx — no new API. Pure-CSS animations + Recharts
 * built-in entry animation; no extra dependencies.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { ClientLogo } from "@/components/Layout";
import { TrendingUp, AlertTriangle, Clock4, CheckCircle2, Layers } from "lucide-react";

const STATUS_META = {
  "Not Started": { color: "#94a3b8",  label: "Not started" },
  "In Progress": { color: "#0a2540",  label: "In progress" },
  "Completed":   { color: "#10b981",  label: "Completed" },
};

/** Count-up hook — animates an integer from 0 to `target` over `ms` ms. */
function useCountUp(target, ms = 900) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (typeof target !== "number") return;
    if (target === 0) { setN(0); return; }
    const start = performance.now();
    let frame;
    const step = (t) => {
      const p = Math.min(1, (t - start) / ms);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * target));
      if (p < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [target, ms]);
  return n;
}

export default function PortfolioOverview({ allProjects, clients, user }) {
  // Role-aware filtering: Executives see only their assigned clients
  // (assigned_client_ids) or projects they're explicitly an assignee on.
  // Admin / Manager see the firm-wide portfolio.
  const { scopedProjects, scopedClients, isPersonal } = useMemo(() => {
    if (!user || user.role === "Admin" || user.role === "Manager") {
      return { scopedProjects: allProjects, scopedClients: clients, isPersonal: false };
    }
    const myClientIds = new Set(user.assigned_client_ids || []);
    const projs = allProjects.filter(
      (p) => myClientIds.has(p.client_id) || (p.assignee_ids || []).includes(user.user_id),
    );
    // Build the client set from the projects we ended up with, in case the
    // executive is an assignee on a project for a client they aren't directly
    // assigned to.
    const visibleClientIds = new Set(projs.map((p) => p.client_id));
    const cls = clients.filter((c) => visibleClientIds.has(c.id));
    return { scopedProjects: projs, scopedClients: cls, isPersonal: true };
  }, [allProjects, clients, user]);

  const stats = useMemo(() => computeStats(scopedProjects, scopedClients),
                        [scopedProjects, scopedClients]);

  if (scopedProjects.length === 0) {
    return null;
  }

  return (
    <section className="px-4 sm:px-8 pt-6" data-testid="portfolio-overview">
      <div className="flex items-end justify-between mb-4">
        <div>
          <h2 className="text-[10px] tracking-[0.18em] uppercase text-slate-500 font-semibold">
            {isPersonal ? "Your Portfolio" : "Portfolio Overview"}
          </h2>
          <div className="text-sm text-slate-500 mt-0.5">
            {stats.totalProjects} project{stats.totalProjects === 1 ? "" : "s"}
            {" · "}{stats.clientsWithWork} client{stats.clientsWithWork === 1 ? "" : "s"} with open work
            {isPersonal && <span className="text-slate-400"> · personal view</span>}
          </div>
        </div>
        <Link to="/clients" className="text-xs text-[#0a2540] hover:underline">
          {isPersonal ? "View my clients →" : "View all clients →"}
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-4">
        {/* LEFT — big status ring + headline counters */}
        <div className="bg-white border border-slate-200 rounded-sm p-5 fade-in-up" style={{ animationDelay: "0ms" }}>
          <div className="grid grid-cols-[160px_1fr] gap-4 items-center">
            <StatusDonut stats={stats} />
            <div className="space-y-3">
              <Counter label="In progress" value={stats.byStatus["In Progress"] || 0}
                       color={STATUS_META["In Progress"].color}
                       testId="overview-inprogress" />
              <Counter label="Not started" value={stats.byStatus["Not Started"] || 0}
                       color={STATUS_META["Not Started"].color}
                       testId="overview-notstarted" />
              <Counter label="Completed" value={stats.byStatus["Completed"] || 0}
                       color={STATUS_META["Completed"].color}
                       testId="overview-completed" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-5 pt-4 border-t border-slate-200">
            <MiniStat icon={AlertTriangle} label="Overdue"   value={stats.overdue}
                      tone={stats.overdue > 0 ? "red" : "slate"}
                      testId="overview-overdue" />
            <MiniStat icon={Clock4}        label="Due ≤ 7d"  value={stats.dueSoon}
                      tone={stats.dueSoon > 0 ? "amber" : "slate"}
                      testId="overview-duesoon" />
            <MiniStat icon={TrendingUp}    label="Stuck"     value={stats.stuck}
                      tone={stats.stuck > 0 ? "amber" : "slate"}
                      testId="overview-stuck" />
          </div>
        </div>

        {/* RIGHT — per-client rings */}
        <div className="bg-white border border-slate-200 rounded-sm p-5 fade-in-up" style={{ animationDelay: "80ms" }}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-slate-500 font-semibold">
                Pending projects · top clients
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {stats.perClient.length} clients with open work
              </div>
            </div>
          </div>
          {stats.perClient.length === 0 ? (
            <div className="py-8 text-center text-xs text-slate-400">
              <CheckCircle2 size={20} className="mx-auto text-emerald-400 mb-1" />
              All clients caught up.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {stats.perClient.slice(0, 8).map((c, i) => (
                <ClientRing key={c.id} client={c} delay={120 + i * 50} />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function StatusDonut({ stats }) {
  const data = ["In Progress", "Not Started", "Completed"]
    .map((s) => ({ name: s, value: stats.byStatus[s] || 0, color: STATUS_META[s].color }))
    .filter((d) => d.value > 0);

  const active = (stats.byStatus["In Progress"] || 0) + (stats.byStatus["Not Started"] || 0);
  const activeAnimated = useCountUp(active, 1000);

  return (
    <div className="relative w-[160px] h-[160px]">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            cx="50%" cy="50%"
            innerRadius={52}
            outerRadius={74}
            paddingAngle={2}
            dataKey="value"
            startAngle={90}
            endAngle={-270}
            stroke="none"
            isAnimationActive
            animationDuration={900}
          >
            {data.map((d, i) => <Cell key={i} fill={d.color} />)}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <div className="text-3xl font-bold text-[#0a2540] font-mono-data" data-testid="overview-active-count">
          {activeAnimated}
        </div>
        <div className="text-[10px] tracking-[0.14em] uppercase text-slate-400 mt-0.5">Active</div>
      </div>
    </div>
  );
}

function Counter({ label, value, color, testId }) {
  const n = useCountUp(value, 900);
  return (
    <div className="flex items-center justify-between" data-testid={testId}>
      <div className="flex items-center gap-2 min-w-0">
        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
        <span className="text-xs text-slate-600 truncate">{label}</span>
      </div>
      <span className="text-lg font-mono-data font-semibold text-[#0a2540] tabular-nums">
        {n}
      </span>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, tone, testId }) {
  const n = useCountUp(value, 700);
  const toneClasses = {
    red:   "text-red-600 bg-red-50 border-red-100",
    amber: "text-amber-700 bg-amber-50 border-amber-100",
    slate: "text-slate-500 bg-slate-50 border-slate-200",
  };
  const isAlert = tone === "red" && value > 0;
  return (
    <div className={`border rounded-sm px-2.5 py-2 ${toneClasses[tone]}`} data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[9px] tracking-wide uppercase opacity-80 font-semibold">
        <Icon size={10} className={isAlert ? "animate-pulse" : ""} />
        {label}
      </div>
      <div className="text-lg font-mono-data font-semibold mt-0.5 tabular-nums">{n}</div>
    </div>
  );
}

function ClientRing({ client, delay = 0 }) {
  // Ring shows pending vs total → animated stroke-dashoffset.
  const total = client.total;
  const pending = client.pending;
  const pct = total > 0 ? Math.round((pending / total) * 100) : 0;
  const r = 22;
  const c = 2 * Math.PI * r;
  const dash = useCountUp(c - (c * pct) / 100, 1000); // animate from full to target
  const pendingAnimated = useCountUp(pending, 900);
  const tone = pending === 0 ? "#10b981" : pending >= 5 ? "#dc2626" : "#0a2540";
  const navigate = useNavigate();

  // Outer card is a clickable div (not a Link) so the inner Dot links can be
  // real <Link>s without nesting <a> inside <a> (invalid HTML).
  const goTo = (filter) => {
    const qs = filter ? `?tab=projects&filter=${filter}` : "?tab=projects";
    navigate(`/clients/${client.id}${qs}`);
  };

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={() => goTo(null)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); goTo(null); } }}
      className="border border-slate-200 rounded-sm p-3 hover:border-[#0a2540] hover:shadow-sm transition-all duration-200 group fade-in-up bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#0a2540]/30"
      style={{ animationDelay: `${delay}ms` }}
      data-testid={`portfolio-client-${client.id}`}
    >
      <div className="flex items-center gap-2.5">
        <div className="relative w-[58px] h-[58px] flex-shrink-0">
          <svg width="58" height="58" viewBox="0 0 58 58" className="-rotate-90">
            <circle cx="29" cy="29" r={r} fill="none" stroke="#f1f5f9" strokeWidth="5" />
            <circle
              cx="29" cy="29" r={r} fill="none"
              stroke={tone} strokeWidth="5" strokeLinecap="round"
              strokeDasharray={c}
              strokeDashoffset={dash}
              style={{ transition: "stroke 200ms ease" }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-base font-bold font-mono-data tabular-nums" style={{ color: tone }}>
              {pendingAnimated}
            </div>
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 min-w-0">
            <ClientLogo fileId={client.logo_file_id} name={client.name} size={14} />
            <div className="text-[11px] font-semibold text-[#0a2540] truncate group-hover:text-[#0a2540]" title={client.name}>
              {client.name}
            </div>
          </div>
          <div className="text-[9px] text-slate-400 uppercase tracking-wide mt-1 font-semibold">
            {pending}/{total} pending
          </div>
          {/* Status dots row — each one deep-links to a filtered project view */}
          <div className="flex items-center gap-1 mt-1.5">
            {(client.statusBreakdown["In Progress"] || 0) > 0 && (
              <Dot clientId={client.id} filter="in_progress"
                   color={STATUS_META["In Progress"].color}
                   count={client.statusBreakdown["In Progress"]} title="In progress" />
            )}
            {(client.statusBreakdown["Not Started"] || 0) > 0 && (
              <Dot clientId={client.id} filter="not_started"
                   color={STATUS_META["Not Started"].color}
                   count={client.statusBreakdown["Not Started"]} title="Not started" />
            )}
            {client.overdue > 0 && (
              <Dot clientId={client.id} filter="overdue"
                   color="#dc2626" count={client.overdue} title="Overdue" pulse />
            )}
            {client.stuck > 0 && (
              <Dot clientId={client.id} filter="stuck"
                   color="#d97706" count={client.stuck} title="Stuck"
                   icon={<AlertTriangle size={9} className="inline" />} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Dot({ clientId, filter, color, count, title, pulse, icon }) {
  return (
    <Link
      to={`/clients/${clientId}?tab=projects&filter=${filter}`}
      onClick={(e) => e.stopPropagation()}
      title={`${title}: ${count} — click to filter`}
      className="flex items-center gap-0.5 hover:opacity-80 hover:scale-110 transition-transform px-0.5 -mx-0.5 rounded"
      data-testid={`portfolio-dot-${clientId}-${filter}`}
    >
      {icon ? (
        <span className={pulse ? "animate-pulse" : ""} style={{ color }}>{icon}</span>
      ) : (
        <span
          className={`w-1.5 h-1.5 rounded-full ${pulse ? "animate-pulse" : ""}`}
          style={{ backgroundColor: color }}
        />
      )}
      <span className="text-[8px] text-slate-500 font-mono-data tabular-nums">{count}</span>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Stats computation — pure, runs on whatever projects+clients are passed in.
// ---------------------------------------------------------------------------

function computeStats(projects, clients) {
  const today = new Date().toISOString().slice(0, 10);
  const in7 = new Date(); in7.setDate(in7.getDate() + 7);
  const in7Str = in7.toISOString().slice(0, 10);
  const byStatus = {};
  let overdue = 0, dueSoon = 0, stuck = 0;
  const perClientMap = {};

  for (const p of projects) {
    byStatus[p.status] = (byStatus[p.status] || 0) + 1;
    const isOpen = p.status !== "Completed";
    if (isOpen && p.due_date && p.due_date < today) overdue++;
    else if (isOpen && p.due_date && p.due_date >= today && p.due_date <= in7Str) dueSoon++;
    if (isOpen && p.stuck_with && p.stuck_with !== "None") stuck++;

    const cid = p.client_id;
    if (!cid) continue;
    if (!perClientMap[cid]) {
      perClientMap[cid] = {
        id: cid, total: 0, pending: 0, overdue: 0, stuck: 0,
        statusBreakdown: {},
      };
    }
    const row = perClientMap[cid];
    row.total += 1;
    if (isOpen) row.pending += 1;
    row.statusBreakdown[p.status] = (row.statusBreakdown[p.status] || 0) + 1;
    if (isOpen && p.due_date && p.due_date < today) row.overdue += 1;
    if (isOpen && p.stuck_with && p.stuck_with !== "None") row.stuck += 1;
  }

  const clientById = new Map(clients.map((c) => [c.id, c]));
  const perClient = Object.values(perClientMap)
    .filter((r) => r.pending > 0)
    .map((r) => {
      const c = clientById.get(r.id);
      return { ...r, name: c?.name || "Unknown client", logo_file_id: c?.logo_file_id };
    })
    .sort((a, b) => b.pending - a.pending || b.overdue - a.overdue);

  return {
    totalProjects: projects.length,
    clientsWithWork: perClient.length,
    byStatus,
    overdue, dueSoon, stuck,
    perClient,
  };
}
